import Base from '@studiometa/js-toolkit';
import transition from '@studiometa/js-toolkit/utils/css/transition';
import nextFrame from '@studiometa/js-toolkit/utils/nextFrame';
import { isDev, useDrupal } from '../config';
import FilterSelect from './Select/Filter';
import { replace } from '../utils/history';

/**
 * Filters class
 * @see form--filters.html.twig
 */
export default class Filters extends Base {
  /**
   * Component configuration
   */
  get config() {
    return {
      name: 'Filters',
      log: isDev(),
      isOpen: false,
      components: {
        FilterSelect,
      },
      shouldUpdateUrl: false,
    };
  }

  /**
   * Mounted hook.
   */
  mounted() {
    this.initUrlSynchronization();
  }

  /**
   * Destroyed hook.
   */
  destroyed() {
    this.destroyUrlSynchronization();
  }

  /**
   * On data-ref="open" click Open the filters panel
   */
  onOpenClick() {
    this.openFilters();
  }

  /**
   * On data-ref="close" click Close the filters panel
   */
  onCloseClick() {
    this.closeFilters();
  }

  /**
   * On data-ref="reset" click Reset all the values
   */
  async onResetClick() {
    // Event listened in Select.vue
    document.body.dispatchEvent(new Event('resetFilters'));

    // Wait for input reset to reset all the form values
    await nextFrame();

    this.$children.FilterSelect.forEach((el) => {
      // Trigger change on <select> to update results
      el.$refs.select.dispatchEvent(new Event('change'));
    });
  }

  /**
   * Close the filters on `ESC`.
   *
   * @param  {Boolean} options.isUp   Is it a keyup event?
   * @param  {Boolean} options.ESC    Is it the ESC key?
   * @return {void}
   */
  keyed({ isUp, ESC }) {
    if (!this.isOpen) {
      return;
    }

    if (ESC && isUp) {
      this.closeFilters();
    }
  }

  /**
   * Open filters panel
   */
  async openFilters() {
    // Animation.
    await transition(
      this.$refs.filters,
      {
        from: 'translate-y-full',
        active: 'transition duration-500 ease-out-expo',
        to: 'translate-y-0',
      },
      'keep'
    );

    // Block scroll.
    document.body.classList.add('overflow-hidden');
    this.isOpen = true;
  }

  /**
   * Close filters panel
   */
  async closeFilters() {
    // Animation.
    await transition(
      this.$refs.filters,
      {
        from: 'translate-y-0',
        active: 'transition duration-500 ease-out-expo',
        to: 'translate-y-full',
      },
      'keep'
    );

    // Enable scroll.
    document.body.classList.remove('overflow-hidden');
    this.isOpen = false;
  }

  /**
   * Init the behavior to update the URL with the form parameters when changing a value.
   */
  initUrlSynchronization() {
    if (!this.$options.shouldUpdateUrl) {
      return;
    }

    const Drupal = useDrupal();
    Drupal.behaviors[this.$id] = {
      attach: (ctx) => {
        if (ctx === this.$el) {
          const params = Object.fromEntries(
            Array.from(new FormData(ctx).entries())
              // Filter out private fields
              .filter(([name]) => !['form_id', 'form_build_id', 'form_token'].includes(name))
              // Remove empty values
              .map(([name, value]) => [name, value === '-1' ? null : value])
          );

          // Remove the pagination from the URL
          let { pathname } = window.location;
          const paginationRegex = /\/[0-9]+$/;
          if (paginationRegex.test(pathname)) {
            pathname = pathname.replace(paginationRegex, '/');
          }

          replace({ pathname, search: params });
        }
      },
    };
  }

  /**
   * Destroy the URL update behavior.
   */
  destroyUrlSynchronization() {
    const Drupal = useDrupal();
    if (Drupal.behaviors[this.$id]) {
      delete Drupal.behaviors[this.$id];
    }
  }
}
